// dashboard-agent-ia/src/components/PinInput.js
import React, { useRef, useEffect } from 'react'

const PinInput = ({ value = '', onChange, label, id = 'pin', autoFocus = false, error = '' }) => {
  const refs = useRef([])
  const digits = Array.from({ length: 6 }, (_, i) => value[i] || '')

  useEffect(() => {
    if (autoFocus) setTimeout(() => refs.current[0]?.focus(), 100)
  }, [autoFocus])

  const handleChange = (idx, e) => {
    const raw = e.target.value.replace(/\D/g, '')

    // Champ vidé (l'utilisateur efface) → mettre à jour sans bloquer
    if (!raw) {
      const next = [...digits]
      next[idx] = ''
      onChange(next.join(''))
      return
    }

    // Gérer le paste de plusieurs chiffres
    if (raw.length > 1) {
      const chars = raw.slice(0, 6).split('')
      const next = [...digits]
      chars.forEach((c, i) => { if (i < 6) next[i] = c })
      onChange(next.join(''))
      const lastIdx = Math.min(chars.length - 1, 5)
      setTimeout(() => refs.current[lastIdx]?.focus(), 10)
      return
    }

    const next = [...digits]
    next[idx] = raw.slice(-1)
    onChange(next.join(''))
    if (raw && idx < 5) setTimeout(() => refs.current[idx + 1]?.focus(), 10)
  }

  const handleKeyDown = (idx, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault()
      const next = [...digits]
      if (next[idx]) {
        // Case pleine → on l'efface et on reste dessus
        next[idx] = ''
        onChange(next.join(''))
      } else if (idx > 0) {
        // Case vide → on remonte et on efface la précédente
        next[idx - 1] = ''
        onChange(next.join(''))
        setTimeout(() => refs.current[idx - 1]?.focus(), 10)
      }
    }
    if (e.key === 'Delete') {
      e.preventDefault()
      const next = [...digits]
      next[idx] = ''
      onChange(next.join(''))
    }
    if (e.key === 'ArrowLeft' && idx > 0) { e.preventDefault(); refs.current[idx - 1]?.focus() }
    if (e.key === 'ArrowRight' && idx < 5) { e.preventDefault(); refs.current[idx + 1]?.focus() }
  }

  // Paste handler global sur le conteneur
  const handlePaste = (e) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    const next = Array.from({ length: 6 }, (_, i) => pasted[i] || '')
    onChange(next.join(''))
    const lastIdx = Math.min(pasted.length - 1, 5)
    setTimeout(() => refs.current[lastIdx]?.focus(), 10)
  }

  const filledCount = digits.filter(d => d !== '').length

  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-3 text-center">{label}</label>}
      <div className="flex gap-3 justify-center" onPaste={handlePaste}>
        {digits.map((digit, idx) => (
          <input
            key={idx}
            ref={el => refs.current[idx] = el}
            id={`${id}-${idx}`}
            type="password"
            inputMode="numeric"
            maxLength={6}
            value={digit}
            onChange={e => handleChange(idx, e)}
            onKeyDown={e => handleKeyDown(idx, e)}
            onFocus={e => e.target.select()}
            autoComplete="off"
            className={`w-12 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none transition-all bg-white text-gray-800
              ${error ? 'border-red-400 bg-red-50' : digit ? 'border-blue-500 bg-blue-50' : 'border-blue-300'}
              focus:border-blue-600 focus:ring-2 focus:ring-blue-200`}
          />
        ))}
      </div>
      <div className="flex gap-1.5 justify-center mt-3">
        {digits.map((d, i) => (
          <div key={i} className={`h-1 w-8 rounded-full transition-all ${i < filledCount ? 'bg-blue-500' : 'bg-gray-200'}`} />
        ))}
      </div>
      {error && <p className="text-red-500 text-xs mt-2 text-center">⚠️ {error}</p>}
    </div>
  )
}

export default PinInput
